import { useEffect } from 'react';
import {
  type MediaItem,
  chooseClip,
  choosePoster,
  downloadName,
  formatDuration,
  formatRange,
  formatTime,
} from '@seemantham/core';
import { background, placeholder, stageWidth, stillKey } from '../lib/data';
import { ChevronLeft, Download } from './icons';

interface PhotoViewProps {
  item: MediaItem;
  items: MediaItem[];
  chapterLabel?: string;
  onBack: () => void;
  onMove: (delta: number) => void;
  onShowInDay: () => void;
}

export function PhotoView({ item, items, chapterLabel, onBack, onMove, onShowInDay }: PhotoViewProps) {
  const index = items.findIndex((candidate) => candidate.id === item.id);
  const position = `${index + 1} of ${items.length}`;

  // The next and previous item start loading before anyone swipes (US-05).
  useEffect(() => {
    for (const delta of [1, -1]) {
      const neighbour = items[index + delta];
      const key = stillKey(neighbour, stageWidth());
      if (neighbour && key) {
        const img = new Image();
        img.src = background(undefined, key).replace(/^url\(|\)$/g, '');
      }
    }
  }, [index, items]);

  const isClip = item.kind === 'clip';
  const clipSource = isClip ? chooseClip(item.renditions, window.innerWidth, window.devicePixelRatio) : undefined;
  const clipPoster = isClip ? choosePoster(item.renditions) : undefined;

  return (
    <div className="app viewer">
      <div className="viewer__bar">
        <button type="button" onClick={onBack} aria-label="Back">
          <ChevronLeft />
        </button>
        <span className="viewer__time">{formatTime(item.capturedAt)}</span>
        <span className="viewer__count" aria-live="polite">
          {position}
        </span>
      </div>

      {isClip && clipSource ? (
        <video
          className="viewer__stage"
          controls
          playsInline
          preload="metadata"
          poster={clipPoster ? `/${clipPoster.key}` : undefined}
          style={{ background: placeholder(item.thumb) ?? 'var(--raised)', backgroundSize: 'cover' }}
          aria-label={`Clip, ${formatTime(item.capturedAt)}, ${formatDuration(item.durationSec ?? 0)}`}
        >
          <source src={`/${clipSource.key}`} type="video/mp4" />
        </video>
      ) : (
        <div
          className="viewer__stage"
          role="img"
          aria-label={`Photo, ${formatTime(item.capturedAt)}`}
          style={{ background: background(item.thumb, stillKey(item, stageWidth())) }}
        />
      )}

      {chapterLabel ? <p className="viewer__chapter">{chapterLabel}</p> : null}

      <div className="viewer__actions">
        <a
          className="button--primary"
          href={`/original/${isClip ? 'clip' : 'photo'}/${item.id}`}
          download={downloadName(item.capturedAt, undefined, isClip ? 'mp4' : 'jpg')}
        >
          <Download />
          {isClip ? 'Save clip' : 'Save photo'}
        </a>
        <div className="actions__secondary">
          <button type="button" className="button--text" onClick={() => void share(item)}>
            Share
          </button>
          <button type="button" className="button--text" onClick={onShowInDay}>
            Show in the day
          </button>
          <button type="button" className="button--text" onClick={() => onMove(1)} aria-label="Next">
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

async function share(item: MediaItem): Promise<void> {
  const url = `${window.location.origin}/m/${item.id}`;
  try {
    if (navigator.share) {
      await navigator.share({ url, title: 'Seemantham' });
      return;
    }
    await navigator.clipboard?.writeText(url);
  } catch {
    // Someone dismissing the share sheet is not an error worth showing.
  }
}

export function chapterLabelFor(startsAt?: string, endsAt?: string): string | undefined {
  return startsAt && endsAt ? formatRange(startsAt, endsAt) : undefined;
}
